import { Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useGallerySystem } from '../../hooks/useGallerySystem';

export function GalleryImageUploader() {
  const { chatbotPublicId } = useChatbot();
  const { uploadImage, error } = useGallerySystem();
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!chatbotPublicId || !e.dataTransfer.files || e.dataTransfer.files.length === 0) return;

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      for (const file of files) {
        await uploadImage(chatbotPublicId, file);
      }
    } finally {
      setIsUploading(false);
    }
  }, [chatbotPublicId, uploadImage]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!chatbotPublicId || !e.target.files || e.target.files.length === 0) return;

    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );

    if (files.length === 0) return;
    
    setIsUploading(true);
    try {
      for (const file of files) {
        await uploadImage(chatbotPublicId, file);
      }
    } finally {
      setIsUploading(false);
    }

    e.target.value = '';
  }, [chatbotPublicId, uploadImage]);

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 transition-colors ${
          dragActive
            ? 'border-indigo-500 bg-indigo-500/10'
            : 'border-white/10 hover:border-indigo-500/50'
        }`}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />

        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-400">
            Drag and drop your images here or click to select
          </p>
          <p className="mt-1 text-xs text-gray-500">
            PNG, JPG, GIF up to 10MB
          </p>
        </div>

        {isUploading && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
            <div className="text-white text-sm flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Uploading...
            </div>
          </div>
        )}
      </div>
    </div>
  );
}